import { getAuthHeaders } from '@/identity.js';
import type { GenerationPlan } from '@/features/quality/GenerationComposer.js';

export interface GenerationPreparationFileRef {
  filename: string;
  storage_ref: string;
  provenance:
    | { type: 'owned_upload' }
    | { type: 'posted_upload'; message_id: string; position: number }
    | { type: 'assistant_file'; message_id: string; file_key: string };
}
export interface GenerationPreparationSnapshot {
  version: 1;
  project_id: string;
  snapshot_revision: number;
  generation_plan: GenerationPlan;
  selected_files: (GenerationPreparationFileRef & {
    sha256: string;
    byte_length: number;
  })[];
}
export interface GenerationPlanReceipt {
  receipt_id: string;
  conversation_id: string;
  project_id: string;
  dispatch_id: string;
  tool_call_id: string;
  expected_snapshot_revision: number;
  snapshot_revision: number;
  generation_plan: GenerationPlan;
}
export type GenerationOrigin =
  | { state: 'unconsumed' }
  | {
      state: 'launched';
      run_id: string;
      project_id: string;
      generation_origin_conversation_id: string;
      generation_origin_actor_id: string;
      generation_origin_snapshot_revision: number;
    };

/** Committed results carry a complete plan and independent, monotonic snapshot version. */
export function parseGenerationPlanReceipt(
  value: unknown,
): GenerationPlanReceipt | null {
  if (!value || typeof value !== 'object') return null;
  const receipt = value as GenerationPlanReceipt;
  if (
    ![
      'receipt_id',
      'conversation_id',
      'project_id',
      'dispatch_id',
      'tool_call_id',
    ].every(
      (key) => typeof (value as Record<string, unknown>)[key] === 'string',
    ) ||
    !Number.isSafeInteger(receipt.expected_snapshot_revision) ||
    receipt.expected_snapshot_revision < 0 ||
    receipt.snapshot_revision !== receipt.expected_snapshot_revision + 1
  )
    return null;
  return validPlan(receipt.generation_plan, receipt.project_id)
    ? receipt
    : null;
}
function validPlan(value: unknown, project: string): value is GenerationPlan {
  const plan = value as GenerationPlan | null;
  return Boolean(
    plan &&
    plan.version === 1 &&
    plan.project_id === project &&
    Number.isSafeInteger(plan.revision) &&
    typeof plan.request === 'string' &&
    Array.isArray(plan.sources) &&
    Array.isArray(plan.skill_ids) &&
    plan.explore &&
    plan.sizing &&
    plan.existing_tests &&
    Array.isArray(plan.unresolved) &&
    plan.provenance,
  );
}
export class GenerationPreparationError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
    public currentSnapshotRevision?: number,
  ) {
    super(message);
  }
}
async function request(
  path: string,
  method = 'GET',
  body?: unknown,
): Promise<unknown> {
  const response = await fetch(path, {
    method,
    headers: {
      ...(await getAuthHeaders()),
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    redirect: 'error',
  });
  const value = await response.json().catch(() => null);
  if (!response.ok)
    throw new GenerationPreparationError(
      typeof value?.message === 'string'
        ? value.message
        : 'The generation preparation could not be synchronized. Your current form has been kept.',
      response.status,
      value?.code,
      value?.current_snapshot_revision,
    );
  return value;
}
function snapshot(value: unknown): GenerationPreparationSnapshot {
  const result = value as GenerationPreparationSnapshot;
  if (
    !result ||
    result.version !== 1 ||
    typeof result.project_id !== 'string' ||
    !Number.isSafeInteger(result.snapshot_revision) ||
    result.snapshot_revision < 0 ||
    !validPlan(result.generation_plan, result.project_id) ||
    !Array.isArray(result.selected_files)
  )
    throw new Error(
      'The generation preparation returned an incompatible snapshot. Your current form has been kept.',
    );
  return result;
}
export async function createGenerationPreparation(
  projectId: string,
  generationPlan: GenerationPlan,
): Promise<{
  conversation_id: string;
  snapshot: GenerationPreparationSnapshot;
}> {
  const value = (await request(
    '/v1/conversations/quality-generation-preparations',
    'POST',
    {
      project_id: projectId,
      generation_plan: generationPlan,
      selected_file_refs: [],
    },
  )) as { conversation_id?: unknown; snapshot?: unknown };
  if (typeof value?.conversation_id !== 'string')
    throw new Error('The generation preparation returned no conversation.');
  return {
    conversation_id: value.conversation_id,
    snapshot: snapshot(value.snapshot),
  };
}
export async function fetchGenerationPreparation(
  id: string,
): Promise<GenerationPreparationSnapshot> {
  return snapshot(
    await request(
      `/v1/conversations/${encodeURIComponent(id)}/quality-generation-preparation`,
    ),
  );
}
export async function updateGenerationPreparation(
  id: string,
  expected: number,
  generationPlan: GenerationPlan,
  files: GenerationPreparationFileRef[],
): Promise<GenerationPreparationSnapshot> {
  return snapshot(
    await request(
      `/v1/conversations/${encodeURIComponent(id)}/quality-generation-preparation`,
      'PUT',
      {
        expected_snapshot_revision: expected,
        generation_plan: generationPlan,
        selected_file_refs: files,
      },
    ),
  );
}
export async function fetchGenerationOrigin(
  id: string,
): Promise<GenerationOrigin> {
  const value = (await request(
    `/v1/quality/generation/origins/${encodeURIComponent(id)}`,
  )) as GenerationOrigin;
  if (value?.state === 'unconsumed') return value;
  if (
    value?.state !== 'launched' ||
    typeof value.run_id !== 'string' ||
    typeof value.project_id !== 'string' ||
    value.generation_origin_conversation_id !== id ||
    typeof value.generation_origin_actor_id !== 'string' ||
    !Number.isSafeInteger(value.generation_origin_snapshot_revision)
  )
    throw new Error(
      'The generation origin could not be resolved. Keep this preparation and retry.',
    );
  return value;
}
